import { z } from 'zod';
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema } from './common';

/** Realtime channels (SDD §14.1) — five channels, each with its payload contract and cadence. Channel access is authorised
 *  at WS-ticket issue (60s TTL, device-bound); every subscribe re-verifies ownership; per-channel sequence numbers; a gap
 *  means client re-fetch, never server frame replay. */
export const CHANNEL_KINDS = ['prices', 'trades', 'session', 'engine', 'notify'] as const;
export const ChannelKindSchema = z.enum(CHANNEL_KINDS);
export type ChannelKind = z.infer<typeof ChannelKindSchema>;

export const PricesPayloadSchema = z
  .object({
    symbol: z.string(),
    /** Where this price came from (owner directive 2026-08-27 §3: every quote carries provider provenance, and
     *  bars from two providers are never spliced into one series). `sim` never reaches a production channel —
     *  the sim binding publishes nothing, precisely so a missing provider is a silent channel, not a fake one. */
    provenance: z.enum(['twelvedata', 'sim']),
    /** Twelve Data's price stream carries ONE price. `bid` and `ask` mirror `last` when the provider gives no
     *  book — equal values mean "spread unknown", and no consumer may render them as a zero spread. */
    bid: z.number(),
    ask: z.number(),
    last: z.number(),
    /** 1s bar when conflated, tick otherwise. */
    bar: z
      .object({
        open: z.number(),
        high: z.number(),
        low: z.number(),
        close: z.number(),
        at: IsoDateTimeSchema,
      })
      .strict()
      .nullable(),
  })
  .strict();
export const TradesPayloadSchema = z
  .object({
    kind: z.enum(['fill', 'position_opened', 'position_updated', 'position_closed', 'order_cancelled']),
    positionId: IdSchema.nullable(),
    orderId: IdSchema.nullable(),
    accountId: IdSchema,
    symbol: z.string(),
    pnl: z.number().nullable(),
  })
  .strict();
export const SessionPayloadSchema = z
  .object({
    kind: z.enum(['state', 'rule_status', 'budget', 'monitor_flag', 'risk_pill', 'heartbeat']),
    sessionId: IdSchema.nullable(),
    state: z.enum(['none', 'preparing', 'armed', 'closing', 'closed', 'bypassed']).nullable(),
    riskState: z.enum(['clear', 'watch', 'warning', 'locked']).nullable(),
    detail: z.record(z.string(), z.unknown()),
  })
  .strict();
export const EnginePayloadSchema = z
  .object({
    kind: z.literal('action_fired'),
    actionId: IdSchema,
    ruleKey: z.string(),
    action: z.string(),
    firedAt: IsoDateTimeSchema,
    detail: z.record(z.string(), z.unknown()),
  })
  .strict();
export const NotifyPayloadSchema = z
  .object({
    kind: z.enum(['item', 'badge']),
    notificationId: IdSchema.nullable(),
    unreadCount: z.number().int().nonnegative(),
    classZero: z.boolean(),
  })
  .strict();

export interface ChannelDescriptor<K extends ChannelKind, P extends z.ZodType, Params> {
  readonly kind: K;
  /** Channel-name pattern as written in SDD §14.1. */
  readonly pattern: string;
  /** Payload the frames on this channel carry. */
  readonly payload: P;
  /** Delivery contract (SDD §14.1 "Contract" column). */
  readonly cadence: string;
  /** Builds the concrete channel name from its parameters. */
  readonly name: (params: Params) => string;
}
function channel<K extends ChannelKind, P extends z.ZodType, Params>(
  d: ChannelDescriptor<K, P, Params>,
): ChannelDescriptor<K, P, Params> {
  return d;
}

export const channels = {
  prices: channel({
    kind: 'prices',
    pattern: 'prices:{symbol}',
    payload: PricesPayloadSchema,
    cadence: 'tick / 1s bar; conflated to client capacity; shared quote cache + per-symbol fetch lease',
    name: (p: { symbol: string }) => `prices:${p.symbol}`,
  }),
  trades: channel({
    kind: 'trades',
    pattern: 'user:{id}:trades',
    payload: TradesPayloadSchema,
    cadence: 'on event (streamed brokers) · ≤ 3s (polling fallback)',
    name: (p: { userId: string }) => `user:${p.userId}:trades`,
  }),
  session: channel({
    kind: 'session',
    pattern: 'user:{id}:session',
    payload: SessionPayloadSchema,
    cadence: 'on event · 5s heartbeat',
    name: (p: { userId: string }) => `user:${p.userId}:session`,
  }),
  engine: channel({
    kind: 'engine',
    pattern: 'user:{id}:engine',
    payload: EnginePayloadSchema,
    cadence: 'on event — engine pushes; no poll-to-notice gap',
    name: (p: { userId: string }) => `user:${p.userId}:engine`,
  }),
  notify: channel({
    kind: 'notify',
    pattern: 'user:{id}:notify',
    payload: NotifyPayloadSchema,
    cadence: 'on event',
    name: (p: { userId: string }) => `user:${p.userId}:notify`,
  }),
} as const;
export type Channels = typeof channels;

/** Wire-level descriptor (what `engine.realtime.channels` returns). */
export const ChannelDescriptorSchema = z
  .object({ kind: ChannelKindSchema, pattern: z.string(), cadence: z.string() })
  .strict();
export type ChannelDescriptorWire = z.infer<typeof ChannelDescriptorSchema>;
export const CHANNEL_DESCRIPTORS: readonly ChannelDescriptorWire[] = CHANNEL_KINDS.map((k) => ({
  kind: k,
  pattern: channels[k].pattern,
  cadence: channels[k].cadence,
}));

/** One frame on a channel: per-channel sequence numbers; a gap ⇒ the client re-fetches. */
export const RealtimeFrameSchema = z
  .object({
    channel: z.string(),
    seq: z.number().int().nonnegative(),
    at: IsoDateTimeSchema,
    payload: z.union([
      PricesPayloadSchema,
      TradesPayloadSchema,
      SessionPayloadSchema,
      EnginePayloadSchema,
      NotifyPayloadSchema,
    ]),
  })
  .strict();
export type RealtimeFrame = z.infer<typeof RealtimeFrameSchema>;

/** WS ticket — 60s TTL, device-bound, issued by `engine.realtime.ticket`. */
export const WsTicketSchema = z
  .object({ ticket: z.string(), expiresAt: IsoDateTimeSchema, channels: z.array(z.string()) })
  .strict();
export type WsTicket = z.infer<typeof WsTicketSchema>;

/** The voice relay wire protocol (SDD §20.2), shared by both ends.
 *
 *  It lives here because the first version of it did not: the server and the client each declared their own copy of
 *  the frame, and the two disagreed about a field within the hour. A wire format with two definitions has no
 *  definition — it has a server's opinion and a client's, and the gap between them is only visible at runtime.
 */
export const VoiceFramePushSchema = z
  .object({ screen: z.string(), params: z.record(z.string(), z.string()) })
  .strict()
  .nullable();

export const VoiceServerEventSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('ready'),
      lane: z.enum(['realtime', 'text']),
      minutes: z.object({ used: z.number(), quota: z.number(), exhausted: z.boolean() }).strict(),
    })
    .strict(),
  z.object({ type: z.literal('turn.start'), turnId: z.string() }).strict(),
  z
    .object({
      type: z.literal('transcript'),
      role: z.enum(['user', 'assistant']),
      text: z.string(),
      final: z.boolean(),
    })
    .strict(),
  z
    .object({
      type: z.literal('turn.end'),
      turnId: z.string(),
      skill: z.string().nullable(),
      refused: z.string().nullable(),
      /** Every frame that carries an answer says whether a simulated lane produced it. A spoken answer that sounds
       *  real and is not is the worst possible place for that to be implicit. */
      simulated: z.boolean(),
      /** The screen the answer belongs on (§20.3) — the same mapping the ⌘K composer uses, so an answer that arrives
       *  by voice leaves the trader where one that arrives by typing would. */
      push: VoiceFramePushSchema,
    })
    .strict(),
  z.object({ type: z.literal('cancelled'), turnId: z.string(), reason: z.literal('barge-in') }).strict(),
  z.object({ type: z.literal('error'), message: z.string() }).strict(),
]);
export type VoiceServerEvent = z.infer<typeof VoiceServerEventSchema>;

export const VoiceClientEventSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('utterance'),
      text: z.string().min(1).max(2000),
      idempotencyKey: IdempotencyKeySchema,
    })
    .strict(),
  /** Barge-in, sent as a message rather than a disconnect: interrupting must not cost a reconnection. */
  z.object({ type: z.literal('interrupt') }).strict(),
  z.object({ type: z.literal('bye') }).strict(),
]);
export type VoiceClientEvent = z.infer<typeof VoiceClientEventSchema>;
